import {Directionality} from '@angular/cdk/bidi';
import {ChangeDetectionStrategy, Component, computed, DestroyRef, ElementRef, HostListener, inject, OnInit} from '@angular/core';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {FormsModule} from '@angular/forms';
import {FilterDefinition, FILTERS_CONFIG, GlobalSearchCategory, GlobalSearchDataStore} from '@global-search/data-access';
import {TranslateModule} from '@ngx-translate/core'; // Import TranslateService
import {IconComponent} from '@shared/icon';
import {hostBinding} from 'ngxtension/host-binding';
import {ChipsModule, ChipsRemoveEvent} from 'primeng/chips';
import {FilterPanelComponent} from '../../components/filter-panel/filter-panel.component';
import {SearchInputComponent} from '../../components/search-input/search-input.component';
import {SearchResultsComponent} from '../../components/search-results/search-results.component';
import {TabNavigationComponent} from '../../components/tab-navigation/tab-navigation.component';
import {GlobalSearchUiStore} from '../../store/global-search-ui.store';
import {IActiveFilterChip} from './types';

@Component({
	selector: 'dc-global-search-container',
	imports: [SearchInputComponent, SearchResultsComponent, TabNavigationComponent, IconComponent, TranslateModule, FilterPanelComponent, ChipsModule, FormsModule],
	templateUrl: './global-search-container.component.html',
	styleUrl: './global-search-container.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: {
		'attr.role': 'dialog',
		'attr.aria-modal': 'true',
	},
})
export class GlobalSearchContainerComponent implements OnInit {
	/* ------------------------------------------  Types and Constants ------------------------------------------ */
	readonly categories: GlobalSearchCategory[] = [
		GlobalSearchCategory.CYCLE,
		GlobalSearchCategory.USER,
		// Hidden now:
		// GlobalSearchCategory.USER_GROUP,
	];
	readonly GlobalSearchCategory = GlobalSearchCategory;
	/* ------------------------------------------ PROVIDERS / SERVICES ------------------------------------------ */
	protected readonly uiStore = inject(GlobalSearchUiStore);
	protected readonly dataStore = inject(GlobalSearchDataStore);
	// Bind collapsed class for main dialog visibility
	protected readonly $isCollapsed = hostBinding(
		'class.collapsed',
		computed(() => !this.uiStore.isOpen$()),
	);

	/* ------------------------------------------------  Host Bindings --------------------------------------------- */
	// Bind aside-collapsed class for side panel animation state
	protected readonly $isAsideCollapsed = hostBinding(
		'class.aside-collapsed',
		computed(() => !this.uiStore.isFiltersPanelOpen$()),
	);
	// Bind aria-hidden for accessibility
	protected readonly $isAriaHidden = hostBinding(
		'attr.aria-hidden',
		computed(() => (!this.uiStore.isOpen$() ? 'true' : 'false')),
	);
	protected readonly isFiltersPanelOpen = this.uiStore.isFiltersPanelOpen$;
	protected readonly activeFiltersCount = this.uiStore.activeFiltersCount$;
	protected readonly activeFiltersList = this.uiStore.activeFiltersList$;

	/* ------------------------------------------------  Signals ------------------------------------------------ */
	protected readonly pageSize = this.uiStore.pageSize$;
	protected readonly activeCategorySorting = this.uiStore.activeCategorySorting$;
	// We'll create display strings combining label and value.
	protected readonly activeFiltersChips = computed<IActiveFilterChip[]>(() => {
		return this.activeFiltersList().map(chip => {
			const displayValue = chip.displayValue;
			return {
				label: chip.label,
				value: String(displayValue),
				originalChip: chip, // Keep the original chip data for reference
			};
		});
	});
	protected readonly searchTerm = computed(() => this.uiStore.searchInput$());
	protected readonly activeCategory = this.uiStore.activeCategory$;
	protected readonly activeFilters = this.uiStore.activeCategoryFilters$;

	// Transform activeFiltersList for PrimeNG p-chips (which expects strings or objects with a label)
	protected readonly filterDefinitions = computed<FilterDefinition<any, any>[]>(() => {
		return FILTERS_CONFIG[this.activeCategory()] ?? [];
	});
	protected readonly results = computed(() => {
		const category = this.activeCategory();
		const state = this.dataStore.state();
		switch (category) {
			case GlobalSearchCategory.CYCLE:
				return state.cycles.items;
			case GlobalSearchCategory.USER:
				return state.users.items;
			case GlobalSearchCategory.USER_GROUP:
				return state.userGroups.items;
			default:
				return [];
		}
	});
	protected readonly loading = computed(() => {
		const category = this.activeCategory();
		const state = this.dataStore.state();
		switch (category) {
			case GlobalSearchCategory.CYCLE:
				return state.cycles.status === 'loading';
			case GlobalSearchCategory.USER:
				return state.users.status === 'loading';
			case GlobalSearchCategory.USER_GROUP:
				return state.userGroups.status === 'loading';
			default:
				return false;
		}
	});
	protected readonly error = computed(() => {
		const category = this.activeCategory();
		const state = this.dataStore.state();
		switch (category) {
			case GlobalSearchCategory.CYCLE:
				return state.cycles.error;
			case GlobalSearchCategory.USER:
				return state.users.error;
			case GlobalSearchCategory.USER_GROUP:
				return state.userGroups.error;
			default:
				return null;
		}
	});
	protected readonly totalResults = computed(() => {
		const category = this.activeCategory();
		const state = this.dataStore.state();
		switch (category) {
			case GlobalSearchCategory.CYCLE:
				return state.cycles.total;
			case GlobalSearchCategory.USER:
				return state.users.total;
			case GlobalSearchCategory.USER_GROUP:
				return state.userGroups.total;
			default:
				return 0;
		}
	});
	private readonly elementRef = inject(ElementRef);
	private readonly directionality = inject(Directionality);
	// Convert Directionality observable to signal
	protected readonly textDirection = toSignal(this.directionality.change, {initialValue: this.directionality.value});
	private readonly destroyRef = inject(DestroyRef);

	/* ------------------------------------------------  Constructor ------------------------------------------------ */
	constructor() {
		// TODO: Add effects for triggering searches
	}

	/* ----------------------------------------------- Lifecycle Hooks ----------------------------------------------- */
	ngOnInit(): void {
		// Subscribe to directionality changes
		this.directionality.change.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
	}

	/* ------------------------------------------------  Methods ------------------------------------------------ */

	@HostListener('click', ['$event'])
	onHostClick(event: MouseEvent): void {
		if (event.target === this.elementRef.nativeElement) {
			this.closeDialog();
		}
	}

	@HostListener('document:keydown.escape', ['$event'])
	onKeydownHandler(event: KeyboardEvent): void {
		if (this.uiStore.isFiltersPanelOpen$()) {
			this.uiStore.toggleFiltersPanel(false); // Close aside first
		} else if (this.uiStore.isOpen$()) {
			this.closeDialog(); // Then close main dialog
		}
	}

	// --- Event Handlers from Child Components ---
	onSearchTermChanged(term: string): void {
		console.log('Search Term Changed (Container):', term);
		this.uiStore.setSearchInput(term);
	}

	onCategorySelected(category: GlobalSearchCategory): void {
		console.log('Category Selected (Container):', category);
		this.uiStore.setSelectedCategory(category);
		this.uiStore.toggleFiltersPanel(false); // Close filters panel when category changes
	}

	onFilterChanged(event: {filter: FilterDefinition<any>; value: any}): void {
		this.uiStore.updateFilter({
			category: this.activeCategory(),
			filter: event.filter,
			value: event.value,
		});
	}

	onLoadMore(): void {
		console.log('Load More Requested (Container) for category:', this.activeCategory());
		const category = this.activeCategory();
		this.uiStore.loadMore(category); // Call the loadMore method in the store;
	}

	onPageSizeChange(pageSize: number) {
		this.uiStore.setPageSize(this.activeCategory(), pageSize);
	}

	protected closeDialog(): void {
		this.uiStore.setIsOpen(false);
		this.uiStore.toggleFiltersPanel(false); // Also close aside panel
	}

	protected stopPropagation(event: Event): void {
		event.stopPropagation();
		this.uiStore.globalSearchContainerClicked$.emit(event);
	}

	protected toggleFiltersPanel(isOpen?: boolean): void {
		this.uiStore.toggleFiltersPanel(isOpen);
	}

	// Handles the 'remove' event from p-chips
	protected removeFilterChip(event: ChipsRemoveEvent): void {
		// Find the original chip data associated with the removed chip value (which is the label string)
		const removedChipData = this.activeFiltersChips().find(chip => chip.label === event.value.label)?.originalChip;
		if (removedChipData) {
			console.log('Removing filter from chip:', removedChipData);
			this.uiStore.removeFilter({
				category: this.activeCategory(),
				filter: removedChipData.filter,
			});
		} else {
			console.warn('Could not find original chip data for removed chip:', event.value);
		}
	}

	protected onSortByChange(sortBy: string): void {
		this.uiStore.updateSorting(this.activeCategory(), sortBy, this.activeCategorySorting().sortDirection);
	}

	protected onSortDirectionChange(direction: 'asc' | 'desc'): void {
		this.uiStore.updateSorting(this.activeCategory(), this.activeCategorySorting().sortBy, direction);
	}
}
